import { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { InputText } from '../../../components/Inputs/InputText';
import { LoadingAbsolute } from '../../../components/LoadingAbsolute';
import { useApi } from '../../../providers/ApiProvider';
import { HeaderForm } from '../../../components/HeaderForm';
import { ModeFormulaire, Ressource } from '../../../types';
import { GoodApiResponse, ErrorApiResponse } from '../../../types/api';
import { DiplomePrepare } from '../../../types/schematics';
import { Config } from '../../../config/config';
import { Helmet } from 'react-helmet';
import { ReadOnlyNumber } from '../../../components/Inputs/ReadOnlyNumber';
import { handleError } from '../../../utils/ErrorHandler';
import { ReadOnlyText } from '../../../components/Inputs/ReadOnlyText';
import { SubmitSave } from '../../../components/SubmitSave';

type Props = { modeFormulaire: ModeFormulaire };

/**
 * Ce composant doit afficher un formulaire qui à 3 modes de DiplomePreparenement. Les 2 principaux sont la création et l'édition.
 * On a également un mode lecture seule. L'idée c'est de gérer une partie du CRUD.
 * C'est dans ce composant que vont se faire les appels API pour créer, éditer et voir une ressource en particulier.
 *
 * @param modeFormulaire - Mode du formulaire (creer, editer et voir).
 * @returns JSX du composant
 */
export const AdminDiplomePrepareFormScreen: FunctionComponent<Props> = ({ modeFormulaire }) => {
  // On initialise les données dont on va avoir besoin sur cette page

  /**
   * On stocke si on est en train d'utiliser l'API ou non.
   */
  const [apiIsLoading, setApiIsLoading] = useState(false);

  /**
   * On stocke le nom afin de l'afficher en mode édition et voir.
   */
  const [nom, setNom] = useState<DiplomePrepare['nom']>('');

  /**
   * On à besoin d'initialiser le client d'API à l'aide d'un hook personalisé.
   */
  const client = useApi();

  /**
   * Contient un état général des valeurs des champs du formulaire ainsi que les champs qui ont reçu une erreur lors de l'envoi du formulaire.
   */
  const [formInputs, setFormInputs] = useState<{ [K in keyof DiplomePrepare]: { value: DiplomePrepare[K]; error: boolean } }>({
    nom: { value: '', error: false },
    nom_commercial: { value: null, error: false },
    nom_abrege: { value: null, error: false },
    nomenclature_diplome: { value: null, error: false },
    nomenclature_niveau: { value: null, error: false },
    nomenclature_cerfa_2012: { value: null, error: false },
    nomenclature_cerfa_2020: { value: null, error: false },
    yp_code: { value: null, error: false },
    created_at: { value: null, error: false },
  });
  const navigate = useNavigate();
  const params = useParams();

  // On a besoin du useEffect lorsque l'on est en mode édition et lecture pour précharger le formulaire avec les données de la ressource.

  useEffect(() => {
    switch (modeFormulaire) {
      case ModeFormulaire.creer:
        break;
      case ModeFormulaire.voir:
      case ModeFormulaire.editer:
        if (params.id === undefined) {
          navigate('/admin/diplomes-prepares');
        }
        handleShow(parseInt(params.id as string));
        break;

      default:
        break;
    }
  }, [client]);

  /**
   * Retrouve par le réseau la ressource liée à l'ID foruni et la met dans un état local.
   * Ne doit être utilisé qu'en mode édition et lecture seule.
   *
   * @param ressourceId - ID de la ressource dont on veut retrouver les informations.
   */
  const handleShow = (ressourceId: Ressource['id']) => {
    if (apiIsLoading === true) {
      return;
    }

    setApiIsLoading(true);
    client
      .get<GoodApiResponse<DiplomePrepare>>(`/admin/diplomes-prepares/${ressourceId}`)
      .then((reponse) => {
        const updatedInputs = Object.keys(reponse.data.data).reduce((acc, key) => {
          return { ...acc, [key]: { value: reponse.data.data[key as keyof DiplomePrepare], error: false } };
        }, formInputs);
        setFormInputs(updatedInputs);
        setNom(updatedInputs.nom.value);
      })
      .catch((error: ErrorApiResponse) => {
        handleError(error, () => {
          navigate('/admin/diplomes-prepares');
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Poste par le réseau une ressource crée dans le formulaire via un état local.
   * Ne doit être utilisé qu'en mode création.
   */
  const handleStore = () => {
    setApiIsLoading(true);

    let ressource: Partial<DiplomePrepare> = {};
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key as keyof DiplomePrepare]?.value !== null) {
        ressource = { ...ressource, [key]: formInputs[key as keyof DiplomePrepare]?.value };
      }
    });
    client
      .post<GoodApiResponse<DiplomePrepare>, DiplomePrepare>('/admin/diplomes-prepares', ressource as DiplomePrepare)
      .then(() => {
        toast.success(`Création éffectuée`);
        navigate('/admin/diplomes-prepares');
      })
      .catch((error: ErrorApiResponse<DiplomePrepare>) => {
        handleError(error, () => {
          let object = formInputs;
          Object.keys(error.response.data.errors).forEach((key) => {
            if (error.response.data.errors[key as keyof DiplomePrepare]) {
              object = { ...object, [key]: { value: formInputs[key as keyof DiplomePrepare]?.value, error: true } };
            }
          }, formInputs);
          setFormInputs(object);
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Met à jour, par le réseau, une ressource à partir du formulaire via un état local.
   * Ne doit être utilisé qu'en mode édition.
   *
   * @param ressourceId - ID de la ressource dont on veut modifier les informations.
   */
  const handleUpdate = (ressourceId: Ressource['id']) => {
    setApiIsLoading(true);

    let ressource: Partial<DiplomePrepare> = {};
    Object.keys(formInputs).forEach((key) => {
      if (formInputs[key as keyof DiplomePrepare]?.value !== null) {
        ressource = { ...ressource, [key]: formInputs[key as keyof DiplomePrepare]?.value };
      }
    });

    client
      .patch<GoodApiResponse<DiplomePrepare>>(`/admin/diplomes-prepares/${ressourceId}`, ressource)
      .then(() => {
        toast.success(`Modification éffectuée`);
        navigate('/admin/diplomes-prepares');
      })
      .catch((error: ErrorApiResponse<DiplomePrepare>) => {
        handleError(error, () => {
          let object = formInputs;
          Object.keys(error.response.data.errors).forEach((key) => {
            if (error.response.data.errors[key as keyof DiplomePrepare]) {
              object = { ...object, [key]: { value: formInputs[key as keyof DiplomePrepare]?.value, error: true } };
            }
          }, formInputs);
          setFormInputs(object);
        });
      })
      .finally(() => {
        setApiIsLoading(false);
      });
  };

  /**
   * Cette méthode permet de gérer l'envoi du formulaire en DiplomePrepare du mode dans lequel on se trouve.
   * L'envoi ne fait rien si on est en mode voir.
   *
   * @param event - Évenement de l'envoie.
   */
  const handleSubmit = (event: { preventDefault: () => void }): void => {
    event.preventDefault();

    if (apiIsLoading === true) {
      return;
    }

    if (modeFormulaire === ModeFormulaire.creer) {
      handleStore();
    }

    if (modeFormulaire === ModeFormulaire.editer) {
      if (params.id !== undefined) {
        handleUpdate(parseInt(params.id as string));
      }
    }
  };

  /**
   * Cette méthode permet de gérer les changements à l'interieur des champs du formulaire et de gérer les valeurs des champs via un état local.
   *
   * @param field - Champs du formulaire dont on doit stocker la valeur et s'il est en erreur.
   */
  const handleInputChange = (field: keyof DiplomePrepare) => {
    return (event: { target: { value: string } }) => {
      setFormInputs((prev) => ({
        ...prev,
        [field]: { value: event.target.value, error: false },
      }));
    };
  };

  /**
   * Permet d'écrire le mot correspondant au mode de fomulaire. Ce mot s'affiche au début du formulaire juste à coté de la ressource.
   *
   * @param modeFormulaire - Mode du formulaire (creer, editer et voir).
   * @returns Mot à afficher correspondant au mode de formulaire.
   * @throws Lance une erreur si aucun mode formulaire n'est fourni.
   */
  const afficheModeFormulaire = (modeFormulaire: ModeFormulaire) => {
    switch (modeFormulaire) {
      case ModeFormulaire.creer:
        return 'Création';
      case ModeFormulaire.editer:
        return 'Édition';
      case ModeFormulaire.voir:
        return 'Voir';

      default:
        throw new Error("Vous n'avez pas choisi un mode de formulaire adéquat.");
    }
  };

  return (
    <Fragment>
      <Helmet>
        <title>
          {afficheModeFormulaire(modeFormulaire)} diplôme préparé - {Config.app_label}
        </title>
      </Helmet>
      <HeaderForm
        text={`${afficheModeFormulaire(modeFormulaire)} diplôme préparé ${modeFormulaire !== ModeFormulaire.creer ? nom : ''}`}
        returnUrl='/admin/diplomes-prepares'
      />
      <form className='relative' onSubmit={handleSubmit}>
        <div className='bg-white p-5'>
          <div className='container mx-auto bg-white rounded'>
            <div className='mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-7'>
              <div>
                <ReadOnlyText label='Nom' value={formInputs.nom.value as string} error={formInputs.nom.error} />
              </div>
              <div>
                <ReadOnlyText label='Nom abrégé' value={formInputs.nom_abrege.value as string} error={formInputs.nom_abrege.error} />
              </div>
              <div>
                <InputText
                  label='Nom commercial'
                  value={formInputs.nom_commercial.value as string}
                  onChange={handleInputChange('nom_commercial')}
                  error={formInputs.nom_commercial.error}
                />
              </div>
              <div>
                <ReadOnlyText
                  label='Nomenclature Cerfa'
                  value={formInputs.nomenclature_diplome.value as string}
                  error={formInputs.nomenclature_diplome.error}
                />
              </div>
              <div>
                <ReadOnlyText label='Nomenclature Sifa' value={formInputs.nomenclature_niveau.value as string} error={formInputs.nomenclature_niveau.error} />
              </div>
              <div>
                <ReadOnlyText
                  label='Nomenclature Cerfa 2012'
                  value={formInputs.nomenclature_cerfa_2012.value as string}
                  error={formInputs.nomenclature_cerfa_2012.error}
                />
              </div>
              <div>
                <ReadOnlyText
                  label='Nomenclature Cerfa 2020'
                  value={formInputs.nomenclature_cerfa_2020.value as string}
                  error={formInputs.nomenclature_cerfa_2020.error}
                />
              </div>
              <div>
                <ReadOnlyNumber label='YParéo CodeDiplomePrepare' value={formInputs.yp_code.value} error={formInputs.yp_code.error} />
              </div>
            </div>
          </div>

          <div className='container mx-auto w-11/12 xl:w-full'>
            <div className='w-full py-4 sm:px-0 bg-white flex justify-end'>{modeFormulaire !== ModeFormulaire.voir && <SubmitSave />}</div>
          </div>
        </div>
        {apiIsLoading ? <LoadingAbsolute /> : ''}
      </form>
    </Fragment>
  );
};
